import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { Observable, from, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MSAL_SILENT_REDIRECT_URI } from '../../msal-config.factory';

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const msalService = inject(MsalService);

  if (!req.url.includes(environment.apiBaseUrl)) {
    return next(req);
  }

  if (req.headers.has('Authorization')) {
    return next(req);
  }

  const demoToken = sessionStorage.getItem('msal.demo.token');
  if (demoToken) {
    try {
      const tokenData = JSON.parse(demoToken) as Record<string, unknown>;
      const accessToken = tokenData['accessToken'];
      if (typeof accessToken === 'string') {
        const clonedRequest = req.clone({
          setHeaders: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        return next(clonedRequest);
      }
    } catch {
      /* empty */
    }
  }

  if (!msalService.instance.getActiveAccount()) {
    const accounts = msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      msalService.instance.setActiveAccount(accounts[0]);
    } else {
      return next(req);
    }
  }

  return from(
    msalService.acquireTokenSilent({
      scopes: [environment.mslConfig.apiScopes],
      account: msalService.instance.getActiveAccount() ?? undefined,
      redirectUri: MSAL_SILENT_REDIRECT_URI,
    }),
  ).pipe(
    catchError((error) => {
      if (error instanceof InteractionRequiredAuthError) {
        msalService.instance.loginRedirect({
          scopes: [
            'openid',
            'profile',
            'email',
            'offline_access',
            environment.mslConfig.apiScopes,
          ],
        });
      } else {
        console.error('Token acquisition failed:', error);
      }

      return of(null);
    }),
    switchMap((result) => {
      if (!result) {
        return next(req);
      }

      const clonedRequest = req.clone({
        setHeaders: {
          Authorization: `Bearer ${result.accessToken}`,
        },
      });

      return next(clonedRequest);
    }),
  );
};
